/**
 * A aba "Diaristas" da Calculadora Petrella.
 *
 * Papel: reproduz `#ac-tab-content-diarista` (l. 1060–1089) e
 * `atualizarCalculadoraDiarista` (l. 4872–4878): diarista e pagador; mês, ano e
 * data; o quadro com a diária, os dias trabalhados e o subtotal; e o total.
 *
 * Como usar: os campos são de quem monta (`CamposDaDiarista`), que os recebe de
 * volta por `onMudar`. Digitar a diarista passa por `aoEscolherDiarista`, que só
 * preenche a diária vazia e nunca os dias. O pagador aparece e é editável, mas
 * não é gravado em lugar nenhum, como na origem.
 */
import { type Favorecido, MESES_POR_EXTENSO } from '@concierge/shared';

import { mascararDecimalLivre } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import {
  type CamposDaDiarista,
  aoEscolherDiarista,
  comMesEAno,
  diaristasDaCalculadora,
  valorDosCamposDaDiarista,
} from '../calculadora';
import { formatarReais } from '../formato';
import { CLASSE_DO_CONTROLE, CLASSE_DO_SELECT, Campo, LinhaDeCampos } from './kit/campo';
import { CartaoDeTipo } from './kit/cartao-de-tipo';

interface AbaDiaristasProps {
  campos: CamposDaDiarista;
  onMudar: (campos: CamposDaDiarista) => void;
  /** Os favorecidos da casa, para a lista e a diária do perfil. */
  favorecidos: readonly Favorecido[];
}

/**
 * A aba "Diaristas".
 *
 * @param campos - Os campos como estão na tela.
 * @param onMudar - Recebe os campos depois de cada mudança.
 * @param favorecidos - Os favorecidos da casa.
 * @returns Os campos, o quadro e o total.
 */
export function AbaDiaristas({ campos, onMudar, favorecidos }: AbaDiaristasProps) {
  const valor = valorDosCamposDaDiarista(campos);

  return (
    <div>
      <LinhaDeCampos colunas={2}>
        <Campo rotulo="Diarista" id="di-favorecido">
          <input
            id="di-favorecido"
            type="text"
            list="di-favorecidos"
            placeholder="Nome da diarista"
            autoComplete="off"
            value={campos.diarista}
            onChange={(evento) =>
              onMudar(
                aoEscolherDiarista({ ...campos, diarista: evento.target.value }, favorecidos),
              )
            }
            className={CLASSE_DO_CONTROLE}
          />
          <datalist id="di-favorecidos">
            {diaristasDaCalculadora(favorecidos).map((favorecido) => (
              <option key={favorecido.id} value={favorecido.nome} />
            ))}
          </datalist>
        </Campo>
        <Campo rotulo="Pagador (a débito de)" id="di-pagador">
          <input
            id="di-pagador"
            type="text"
            value={campos.pagador}
            onChange={(evento) => onMudar({ ...campos, pagador: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <LinhaDeCampos colunas={3}>
        <Campo rotulo="Mês de competência" id="di-mes">
          <select
            id="di-mes"
            value={campos.mes}
            onChange={(evento) => onMudar(comMesEAno(campos, { mes: evento.target.value }))}
            className={CLASSE_DO_SELECT}
          >
            {MESES_POR_EXTENSO.map((nome, indice) => (
              <option key={nome} value={String(indice + 1)}>
                {nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Ano" id="di-ano">
          <input
            id="di-ano"
            type="number"
            value={campos.ano}
            onChange={(evento) => onMudar(comMesEAno(campos, { ano: evento.target.value }))}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
        <Campo rotulo="Data do lançamento" id="di-data">
          <input
            id="di-data"
            type="date"
            value={campos.data}
            onChange={(evento) => onMudar({ ...campos, data: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <div className="-mt-1 mb-3 text-[11px] text-texto-fraco">
        Diaristas trabalham esporádico — os dias do mês não têm padrão fixo, preencha
        manualmente.
      </div>
      <CartaoDeTipo className="mb-0">
        <div className="grid grid-cols-3 gap-2">
          <Campo rotulo="Valor diária (R$)" id="di-valor-diaria">
            <input
              id="di-valor-diaria"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={campos.diaria}
              onChange={(evento) =>
                onMudar({ ...campos, diaria: mascararDecimalLivre(evento.target.value) })
              }
              className={cn(CLASSE_DO_CONTROLE, 'text-right')}
            />
          </Campo>
          <Campo rotulo="Dias trabalhados" id="di-dias">
            <input
              id="di-dias"
              type="number"
              min={0}
              step={1}
              placeholder="0"
              value={campos.dias}
              onChange={(evento) => onMudar({ ...campos, dias: evento.target.value })}
              className={CLASSE_DO_CONTROLE}
            />
          </Campo>
          <Campo rotulo="Subtotal" id="di-subtotal">
            <div id="di-subtotal" className="py-2 font-bold text-foreground">
              {formatarReais(valor)}
            </div>
          </Campo>
        </div>
      </CartaoDeTipo>
      <div className="mt-3 flex justify-end text-[15px] text-foreground">
        <strong>Total: {formatarReais(valor)}</strong>
      </div>
      <div className="mt-2.5 text-[11px] text-texto-fraco">
        Ao confirmar, cria um lançamento Pendente (Petrella › Diaristas Passadeiras).
      </div>
    </div>
  );
}
